import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useWebAuth } from './auth.js';

/**
 * The web app's current "viewing language", shared across Challenge Creation and
 * Judgement so a choice on one page sticks on the other. Persisted per account
 * (so it survives a reload and the role previewer), and always validated against
 * the account's accessible languages.
 */

interface LanguageValue {
  langs: string[];
  lang: string;
  setLang: (l: string) => void;
}

const Ctx = createContext<LanguageValue | null>(null);

const keyFor = (id: string) => `kolectra.web.lang.${id}`;

function initialLang(id: string, langs: string[]): string {
  try {
    const saved = localStorage.getItem(keyFor(id));
    if (saved && langs.includes(saved)) return saved;
  } catch {
    /* localStorage unavailable */
  }
  return langs[0] ?? '';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { account } = useWebAuth();
  const langs = account.languages;

  const [lang, setLangState] = useState<string>(() => initialLang(account.user.id, langs));

  // The previewer can switch accounts; re-derive a valid selection when it does.
  useEffect(() => {
    setLangState(initialLang(account.user.id, langs));
  }, [account.user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function setLang(l: string) {
    setLangState(l);
    try {
      localStorage.setItem(keyFor(account.user.id), l);
    } catch {
      /* localStorage unavailable */
    }
  }

  // Guard against a stale selection (e.g. right after an account switch).
  const safeLang = langs.includes(lang) ? lang : langs[0] ?? '';

  return <Ctx.Provider value={{ langs, lang: safeLang, setLang }}>{children}</Ctx.Provider>;
}

export function useLanguage(): LanguageValue {
  const c = useContext(Ctx);
  if (!c) throw new Error('useLanguage must be used within <LanguageProvider>.');
  return c;
}
